import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { useFetchEventSessions, SESSIONS_PER_PAGE } from 'actions/eventSessions';
import useSearchParams from 'hooks/useSearchParams';
import createLocation from 'utils/createLocation';
import { locationWithPageNumber, getNumberOfPages, getPageFromQuery } from 'utils/pagination';
import getUsersISOLocale from 'utils/i18n/getUsersISOLocale';
import Landmark from 'components/Landmark';
import TableCard, { Column } from 'components/TableCard';
import Button from 'components/Button';
import Flex, { Box } from 'components/Flex';
import TooltipButton from 'components/TooltipButton';
import TextLink from 'components/TextLink';
import Text from 'components/Text';
import VisuallyHidden from 'components/VisuallyHidden';
import { getFormattedStartDateTime, sessionQueryParameters } from 'screens/EventSessionsManager';
import DeleteSessionModal from './DeleteSessionModal';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function SessionsTable(_ref) {
  var eventId = _ref.eventId,
    startDate = _ref.startDate;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var location = useLocation();
  var _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 1),
    searchParams = _useSearchParams2[0];
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    sessionPendingDelete = _React$useState2[0],
    setSessionPendingDelete = _React$useState2[1];
  var pageQueryParameter = sessionQueryParameters.page;
  var page = getPageFromQuery({
    query: searchParams,
    pageQueryParameter: pageQueryParameter
  });
  var _useFetchEventSession = useFetchEventSessions({
      eventId: eventId,
      startDate: startDate,
      page: page
    }),
    items = _useFetchEventSession.items,
    total = _useFetchEventSession.total,
    isLoading = _useFetchEventSession.isLoading,
    isFetching = _useFetchEventSession.isFetching,
    isPlaceholderData = _useFetchEventSession.isPlaceholderData;
  var pageCount = getNumberOfPages({
    total: total,
    itemsPerPage: SESSIONS_PER_PAGE
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(TableCard, {
      isLoading: isLoading || isFetching && isPlaceholderData,
      emptyContent:
      /*#__PURE__*/
      // Same height as loading state
      _jsx(Landmark, {
        icon: "calendar",
        height: 250,
        children: t('sessions_list_empty_state')
      }),
      data: items,
      pageCount: pageCount,
      currentPage: page,
      getPageLink: function getPageLink(pageNumber) {
        return locationWithPageNumber({
          location: location,
          page: pageNumber,
          pageQueryParameter: pageQueryParameter
        });
      },
      mobileViewBreakpoint: "m",
      align: "top",
      children: [/*#__PURE__*/_jsx(Column, {
        width: "25%",
        title: t('property_start_date'),
        cellRenderer: function cellRenderer(session) {
          var timezone = session.timezone;
          var formattedDateTime = new Intl.DateTimeFormat(getUsersISOLocale(), {
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZone: timezone
          }).format(new Date(session.startDateTime));
          return /*#__PURE__*/_jsxs(Box, {
            children: [/*#__PURE__*/_jsx(Text, {
              display: "block",
              children: formattedDateTime
            }), /*#__PURE__*/_jsx(Text, {
              dimmed: true,
              size: "xs",
              children: timezone
            })]
          });
        }
      }), /*#__PURE__*/_jsx(Column, {
        width: "15%",
        title: t('property_reference'),
        cellRenderer: function cellRenderer(session) {
          return /*#__PURE__*/_jsx("div", {
            children: session.reference
          });
        }
      }), /*#__PURE__*/_jsx(Column, {
        title: t('property_location'),
        cellRenderer: function cellRenderer(session) {
          var isVirtual = session.isVirtual,
            location = session.location;
          return /*#__PURE__*/_jsxs(Box, {
            children: [/*#__PURE__*/_jsx(Text, {
              display: "block",
              children: location
            }), /*#__PURE__*/_jsx(Text, {
              dimmed: true,
              size: "xs",
              children: t(isVirtual ? 'property_virtual' : 'property_in_person')
            })]
          });
        }
      }), /*#__PURE__*/_jsx(Column, {
        width: "15%",
        title: t('property_attendees'),
        cellRenderer: function cellRenderer(session) {
          return /*#__PURE__*/_jsxs(TextLink, {
            as: NavLink,
            bold: true,
            to: createLocation(location, _defineProperty(_defineProperty({}, sessionQueryParameters.id, String(session.id)), sessionQueryParameters.tab, 'attendees')),
            children: [session.attendeesCount, typeof session.capacity === 'number' && "/".concat(session.capacity)]
          });
        }
      }), /*#__PURE__*/_jsx(Column, {
        width: "20%",
        title: t('property_actions'),
        cellRenderer: function cellRenderer(session) {
          var timezone = session.timezone;
          var formattedDateTime = getFormattedStartDateTime({
            timezone: timezone,
            startDateTime: session.startDateTime
          });
          var hasAttendees = session.attendeesCount > 0;
          return /*#__PURE__*/_jsxs(Flex, {
            gap: "xs",
            children: [/*#__PURE__*/_jsxs(Button, {
              size: "small",
              variant: "primary",
              as: NavLink,
              to: createLocation(location, _defineProperty(_defineProperty({}, sessionQueryParameters.id, String(session.id)), sessionQueryParameters.tab, 'details')),
              children: [t('actions_manage'), /*#__PURE__*/_jsxs(VisuallyHidden, {
                children: [' ', formattedDateTime]
              })]
            }), /*#__PURE__*/_jsxs(TooltipButton, {
              isDisabled: hasAttendees,
              iconOnly: true,
              variant: "primary",
              icon: "trash",
              size: "small",
              onClick: function onClick() {
                return setSessionPendingDelete(session);
              },
              children: [t(hasAttendees ? 'hint_session_cannot_be_deleted' : 'actions_delete'), /*#__PURE__*/_jsxs(VisuallyHidden, {
                children: [' ', formattedDateTime]
              })]
            })]
          });
        }
      })]
    }), sessionPendingDelete && /*#__PURE__*/_jsx(DeleteSessionModal, {
      eventId: eventId,
      session: sessionPendingDelete,
      onClose: function onClose() {
        return setSessionPendingDelete(null);
      }
    })]
  });
}
export default SessionsTable;